import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { selectIsAuthenticated } from "@onmo/auth";
import {
  getIsLoggingOut,
  getIsProfileSwitching,
  getProfileChosenAt,
  releaseLoggingOut,
  setProfileSession,
  subscribeProfileSession,
} from "@onmo/graphql";
import {
  generateProfileTokenGQL,
  meProfileGQL,
  Profile as ProfileGQL,
  profilesGQL,
  Query,
} from "@onmo/graphql/graphql";
import { useAppSelector } from "../components/hooks";
import { PageRoutes } from "../constants/routes";
import { toProfile } from "../models/profile";

export const useIsProfileSwitching = () =>
  useSyncExternalStore(subscribeProfileSession, getIsProfileSwitching);

export const useCurrentProfile = (profileId?: string) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: meData } = useQuery<{ me: Query["me"] }>(meProfileGQL, {
    skip: !isAuthenticated,
  });
  const { data, loading } = useQuery<{ profiles: ProfileGQL[] }>(profilesGQL, {
    skip: !isAuthenticated,
    context: { skipProfileToken: true },
  });
  const profiles = data?.profiles ?? [];
  const current =
    meData?.me.profile ?? profiles.find((profile) => profile.isMainAccount);
  const profileById = profileId
    ? profiles.find((profile) => profile.id === profileId)
    : undefined;
  return {
    currentProfile: current ? toProfile(current) : undefined,
    profileById: profileById ? toProfile(profileById) : undefined,
    isLoading: loading,
  };
};

// Kids have no access to Install & Play or Steam
export const useIsKidProfile = () =>
  !!useCurrentProfile().currentProfile?.isKid;

export const useIsMainProfile = () =>
  useCurrentProfile().currentProfile?.isOwner ?? true;

export const useProfilePickerRedirect = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const chosenAt = useSyncExternalStore(
    subscribeProfileSession,
    getProfileChosenAt,
  );
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isLoggingOut = useSyncExternalStore(
    subscribeProfileSession,
    getIsLoggingOut,
  );
  const hasNotChosen = isAuthenticated && !chosenAt && !isLoggingOut;

  useEffect(() => {
    if (!isAuthenticated) releaseLoggingOut();
  }, [isAuthenticated]);
  const { data, error } = useQuery<{ profiles: ProfileGQL[] }>(profilesGQL, {
    skip: !hasNotChosen,
    context: { skipProfileToken: true },
  });
  const profileCount = data?.profiles.length;
  const isOnPicker = pathname === PageRoutes.WhoIsPlaying;
  const hasChoice = profileCount !== undefined && profileCount > 1;
  const hasNoChoice = profileCount !== undefined && profileCount <= 1;
  const mainProfileId = data?.profiles.find(
    (profile) => profile.isMainAccount,
  )?.id;
  const [generateProfileToken] = useMutation<{
    generateProfileToken: { token: string };
  }>(generateProfileTokenGQL, { context: { skipProfileToken: true } });
  const isEnteringMain = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || isOnPicker || !hasNotChosen) return;
    const goToPicker = () => {
      navigate(PageRoutes.WhoIsPlaying, {
        replace: true,
        state: { from: pathname + search },
      });
    };
    if (hasChoice) return goToPicker();
    if (!hasNoChoice || !mainProfileId || isEnteringMain.current) return;
    isEnteringMain.current = true;
    generateProfileToken({ variables: { input: { id: mainProfileId } } })
      .then((result) => {
        const token = result.data?.generateProfileToken?.token;
        if (!token) throw new Error("No profile token returned");
        setProfileSession(token);
      })
      .catch(goToPicker)
      .finally(() => {
        isEnteringMain.current = false;
      });
  }, [
    isAuthenticated,
    hasNotChosen,
    hasChoice,
    hasNoChoice,
    mainProfileId,
    isOnPicker,
    pathname,
    search,
    navigate,
    generateProfileToken,
  ]);

  return hasNotChosen && !isOnPicker && !error;
};
